import type { QueryClient } from '@tanstack/react-query';

import type { DecompositionNode } from 'api/data/projects/types';
import { normalizeId } from 'utils/ids';

import type { TreeCacheScope } from '../types';
import { mapMatchingNodes, removeMatchingNodes } from './nodeListMutations';
import { nodeMatchesId } from './treeCacheScope';
import { treeQueryKeys } from './treeQueryKeys';

/** Mutations on the sticky-top lists only — see `treeQueryKeys.stickyTop` for what a pin owns. */

function matcherFor(nodeId: string): (node: DecompositionNode) => boolean {
  const normalId = normalizeId(nodeId);
  return (node) => nodeMatchesId(node, normalId);
}

export function pushStickyTopNode(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  parentDataNodeId: string | undefined,
  node: DecompositionNode
): void {
  const matches = matcherFor(node.id);

  queryClient.setQueryData<DecompositionNode[]>(treeQueryKeys.stickyTop(scope, parentDataNodeId), (prev) => {
    const list = prev ?? [];
    if (!list.some(matches)) return [node, ...list];
    return mapMatchingNodes(list, matches, (existing) => mergePin(existing, node));
  });
}

/**
 * Fallbacks for fields the incoming pin may omit or zero out. Rendering prefers the server copy
 * once one exists (mergeNodes.ts), but a node the server has never returned still renders from this
 * payload — so a stale `onNodeCreated` re-pin (always `hasChildren: false`, empty title/subtitle)
 * must not clobber values earned from an interleaved relation event. `hasChildren` is
 * authoritatively cleared via `patchHasChildren`/`resetHasChildrenIfEmpty`, not through this merge.
 * `createdThisSession` is sticky for the session: once set, a later pin of the same id without the
 * marker (e.g. a reparent of the page copy) must not drop it.
 */
function mergePin(existing: DecompositionNode, incoming: DecompositionNode): DecompositionNode {
  return {
    ...existing,
    ...incoming,
    title: incoming.title || existing.title,
    subtitle: incoming.subtitle || existing.subtitle,
    hasChildren: incoming.hasChildren || existing.hasChildren,
    createdThisSession: existing.createdThisSession || incoming.createdThisSession
  };
}

/** Patches a node in every sticky-top list of this scope that holds it. */
export function patchStickyTopNode(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  nodeId: string,
  patch: Partial<DecompositionNode>
): void {
  const matches = matcherFor(nodeId);

  queryClient.setQueriesData<DecompositionNode[]>({ predicate: treeQueryKeys.sticky(scope) }, (prev) =>
    mapMatchingNodes(prev, matches, (node) => ({ ...node, ...patch }))
  );
}

export function removeFromStickyTop(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  parentDataNodeId: string | undefined,
  nodeId: string
): void {
  queryClient.setQueryData<DecompositionNode[]>(treeQueryKeys.stickyTop(scope, parentDataNodeId), (prev) =>
    removeMatchingNodes(prev, matcherFor(nodeId))
  );
}

/**
 * Removes a node from EVERY sticky-top list in this scope (root and all parents). Used before
 * re-pinning a moved node, so a reparent can never leave it pinned under both its old and its new
 * parent.
 */
export function removeNodeFromAllStickyTops(queryClient: QueryClient, scope: TreeCacheScope, nodeId: string): void {
  const matches = matcherFor(nodeId);

  queryClient.setQueriesData<DecompositionNode[]>({ predicate: treeQueryKeys.sticky(scope) }, (prev) =>
    removeMatchingNodes(prev, matches)
  );
}

/** True when the node is pinned under a parent rather than at root — i.e. a reparent target. */
export function isNodePinnedUnderNonRootParent(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  nodeId: string
): boolean {
  const matches = matcherFor(nodeId);

  return queryClient
    .getQueriesData<DecompositionNode[]>({ predicate: treeQueryKeys.nonRootSticky(scope) })
    .some(([, list]) => list?.some(matches));
}
